import { useNavigate, useParams } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import CantileverButton, {
  CantileverLinkButton,
  EditButton,
} from 'components/CantileverButton'
import CantileverCard from 'components/CantileverCard'
import CantileverRow from 'components/CantileverRow'
import { Thumbnail } from 'components/table/utility/Thumbnail'
import {
  InspectionOverviewEditorGate,
  InspectionOverviewViewerGate,
} from 'features/assetDatabase'
import { AssetImage } from 'features/assetDatabase/components/assetImage/AssetImage'
import {
  DrawerState,
  inspectionTypeOptions,
  useRightDrawer,
} from 'features/rightDrawer'
import { useRolodexEntries } from 'features/rolodex'
import { ArrowRight } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoInspection,
  GeckoJob,
  GeckoUnit,
  getSortedAssetModels,
  RolodexEntryType,
} from 'types'

import { AssetDetailsTags } from './AssetDetailsTags'
import { CDSTag, Tag, TextSm } from './CDSTag'
import { InspectionHeatmap } from './InspectionHeatmap'
import { JobCardHeader } from './JobCardHeader'

export type InspectionListProps = {
  siteID: string
  inspections: GeckoInspection[]
  job: GeckoJob
  assetModels: ReadonlyArray<GeckoAssetModel>
  units: Map<string, GeckoUnit>
  components: Map<string, GeckoComponent>
  cdfs: ReadonlyArray<GeckoComponentDataFrame>
  cdses: ReadonlyArray<GeckoComponentDataset>
}

export const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`

const StyledRightArrow = styled(ArrowRight)`
  width: 1rem;
  position: relative;
  top: 0.03125rem;
`

const UnlinkedAssetHeader = styled.div`
  min-height: 2rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 0.5rem;
  padding: 0 0.5rem 0 0;
`

const UnitImg: React.FC<{
  primaryImageID: string
  assetModels: GeckoAssetModel[]
}> = ({ primaryImageID, assetModels }) => {
  const effectiveAM = getSortedAssetModels([...assetModels])[0]
  const assetModel = assetModels.length > 0 ? effectiveAM : undefined

  return (
    <Thumbnail>
      <AssetImage
        primaryImageID={primaryImageID}
        assetModelType={assetModel?.data.type}
      />
    </Thumbnail>
  )
}

export const JobCard: React.FC<{
  ilp: InspectionListProps
  linkToAsset?: boolean
  forceRefreshInspections: () => void
  showAddInspection?: boolean
  showJobHandoff?: boolean
}> = ({
  ilp,
  linkToAsset,
  forceRefreshInspections,
  showAddInspection = true,
  showJobHandoff = true,
}) => {
  const navigate = useNavigate()
  const { BASE_PATH } = useRuntimeVariables()
  const { siteID } = useParams({ strict: false })
  const { setDrawerEntry, setDrawerState } = useRightDrawer()

  const sortedUnits = React.useMemo(() => {
    return Array.from(ilp.units.entries()).sort(([keyA], [keyB]) => {
      return keyA.localeCompare(keyB)
    })
  }, [ilp])

  const inspectionsByUnit = React.useMemo(() => {
    return Object.groupBy(ilp.inspections, (inspection) => {
      return inspection.tags.unit
    })
  }, [ilp.inspections])

  const { reports } = useRolodexEntries({
    type: RolodexEntryType.REPORT,
    tags: { site: [ilp.siteID] },
    filters: [{ key: 'file_category', op: '=', value: 'gecko_report' }],
  })

  const handleNavigate = (
    target: GeckoUnit,
    event?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    event?.stopPropagation()
    if (siteID) {
      const navOptions = {
        to: '/assets/$siteID/$unitID',
        params: {
          siteID,
          unitID: target.id,
        },
      }
      if (event?.metaKey || event?.ctrlKey) {
        const leadingURL = BASE_PATH ? `/${BASE_PATH}` : ''
        window.open(`${leadingURL}/assets/${siteID}/${target.id}`, '_blank')
      } else {
        navigate(navOptions)
      }
    }
  }

  const editEntryOnClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    entry: GeckoInspection | GeckoJob,
    drawerState: DrawerState
  ) => {
    setDrawerEntry(entry)
    setDrawerState(drawerState)
    event.stopPropagation()
  }

  function getBestCdfForInspection(
    inspection: GeckoInspection
  ): GeckoComponentDataFrame | undefined {
    const cdfsInInspection = ilp.cdfs.filter(
      (cdf) => cdf.tags.inspection === inspection.id
    )

    return cdfsInInspection
      .toSorted((a, b) => GeckoComponentDataFrame.DefaultSort(a, b, ilp.cdses))
      .at(0)
  }

  return (
    <CantileverCard>
      <JobCardHeader
        job={ilp.job}
        forceRefreshInspections={forceRefreshInspections}
        showAddInspection={showAddInspection}
        showJobHandoff={showJobHandoff}
      />

      {sortedUnits.map(([key, unit], i) => {
        const assetHeaderContent = (
          <>
            {unit.data.primary_image && (
              <UnitImg
                primaryImageID={unit.data.primary_image}
                assetModels={ilp.assetModels.filter(
                  (assetModel) => assetModel.tags.unit === unit.id
                )}
              />
            )}
            <p>{unit.data.name}</p>
          </>
        )

        return (
          <CantileverCard.Section key={key} topBorder={i > 0}>
            <CantileverCard.SectionHeader
              leadingContent={
                (linkToAsset ?? true) ? (
                  <CantileverLinkButton
                    to='/assets/$siteID/$unitID'
                    params={{
                      siteID: siteID ?? '',
                      unitID: unit.id,
                    }}
                    ghost
                    padding='0 0.5rem 0 0'>
                    {assetHeaderContent}
                  </CantileverLinkButton>
                ) : (
                  <UnlinkedAssetHeader>
                    {assetHeaderContent}
                  </UnlinkedAssetHeader>
                )
              }
            />
            {(inspectionsByUnit[unit.id] ?? []).length > 0 && (
              <CantileverCard.Group>
                {inspectionsByUnit[unit.id]?.map((inspection) => {
                  const bestCDFForInspection =
                    getBestCdfForInspection(inspection)

                  return (
                    <CantileverRow
                      key={inspection.id}
                      vPadding='0.5rem'
                      hPadding='0.5rem'
                      leadingContent={
                        <>
                          <InspectionHeatmap
                            unit={unit}
                            inspection={inspection}
                            component={
                              ilp.components.get(
                                inspection.tags.component
                              ) as GeckoComponent
                            }
                            assetModel={ilp.assetModels.find(
                              (assetModel) =>
                                assetModel.tags.unit === inspection.tags.unit
                            )}
                            componentDataFrame={bestCDFForInspection}
                          />
                          <TextSm>
                            {
                              ilp.components.get(inspection.tags.component)
                                ?.data.name
                            }
                          </TextSm>
                          <Tag>
                            {
                              inspectionTypeOptions.find(
                                (option) =>
                                  option.value ===
                                  inspection.data.inspection_type
                              )?.label
                            }
                          </Tag>
                          <CDSTag inspection={inspection} />
                          {reports.filter((report) =>
                            (report.data.inspections ?? []).includes(
                              inspection.id
                            )
                          ).length > 0 ? (
                            <Tag>Report Uploaded</Tag>
                          ) : (
                            <Tag>No Reports</Tag>
                          )}
                          <InspectionOverviewViewerGate>
                            <AssetDetailsTags
                              assetModels={ilp.assetModels}
                              unitId={unit.id}
                              componentId={inspection.tags.component}
                            />
                          </InspectionOverviewViewerGate>
                        </>
                      }
                      trailingContent={
                        <ButtonGroup>
                          <InspectionOverviewEditorGate>
                            {(inspection.data.inspection_type === 'raut' ||
                              inspection.data.inspection_type === 'trilat') && (
                              <CantileverButton
                                onClick={(
                                  event: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                  editEntryOnClick(
                                    event,
                                    inspection,
                                    DrawerState.TriggerRAUTGdriveRolodexSync
                                  )
                                }}>
                                Sync AS Data
                              </CantileverButton>
                            )}
                            <EditButton
                              title='Edit Inspection'
                              onClick={(
                                event: React.MouseEvent<HTMLButtonElement>
                              ) => {
                                editEntryOnClick(
                                  event,
                                  inspection,
                                  DrawerState.EditGeckoInspection
                                )
                              }}
                            />
                          </InspectionOverviewEditorGate>
                          <CantileverLinkButton
                            primary
                            to='/details/$entryID'
                            params={{
                              entryID: inspection.tags.component,
                            }}
                            search={{
                              cdf: bestCDFForInspection?.id,
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            Open in Viewer
                            <StyledRightArrow />
                          </CantileverLinkButton>
                        </ButtonGroup>
                      }
                      onClick={(e) => handleNavigate(unit, e)}
                    />
                  )
                })}
              </CantileverCard.Group>
            )}
          </CantileverCard.Section>
        )
      })}
    </CantileverCard>
  )
}
